import { z } from "zod";

// Catalogue of static rectangle pillow sizes (ADR-0033 §2, #63). A rounded
// rectangle pillow's geometry is pet-independent, so it is baked once by the
// worker and served as a shared static asset (not per job). Mirrors
// worker/src/pufimi_worker/schemas/static_assets.py — keep the two in lockstep.
export const STATIC_RECTANGLE_SIZES = ["40x40", "50x50", "40x60"] as const;
export type StaticRectangleSize = (typeof STATIC_RECTANGLE_SIZES)[number];
export const StaticRectangleSizeSchema = z.enum(STATIC_RECTANGLE_SIZES);

// width/height in cm per size. 40x40 and 50x50 are 1:1; 40x60 is 2:3. The cm are
// the producer/display truth; the baked geometry is normalised (shape + size are
// resolved at render time, ADR-0033 §1), so the two 1:1 meshes are identical.
export const STATIC_RECTANGLE_SIZE_CM: Record<StaticRectangleSize, readonly [number, number]> = {
  "40x40": [40, 40],
  "50x50": [50, 50],
  "40x60": [40, 60],
};

// Fixed S3 prefix for shared static assets — a sibling of `projects/` in the same
// bucket (ADR-0007 single-bucket-with-prefixes layout), fronted by the same CDN.
export const STATIC_RECTANGLES_PREFIX = "static/rectangles/";
export const STATIC_RECTANGLE_MANIFEST_KEY = `${STATIC_RECTANGLES_PREFIX}manifest.json`;
export const STATIC_RECTANGLE_MANIFEST_VERSION = 1;

// Deterministic S3 key for a size's GLB — stable across re-bakes (#63 AC5). The
// api signs this key to hand the frontend a static rectangle mesh URL.
export const staticRectangleKey = (size: StaticRectangleSize): string =>
  `${STATIC_RECTANGLES_PREFIX}${size}.glb`;

// The stable manifest at STATIC_RECTANGLE_MANIFEST_KEY: maps each size to its GLB
// key. camelCase wire convention (ADR-0012). Mirrors StaticRectangleManifest in
// the worker; `version` guards future shape changes.
export const StaticRectangleManifest = z.object({
  version: z.number().int(),
  // Keyed by the size enum so a manifest with a typo'd/unknown size is rejected on
  // read (the worker only ever emits the catalogue keys).
  rectangles: z.record(StaticRectangleSizeSchema, z.string()),
});
export type StaticRectangleManifest = z.infer<typeof StaticRectangleManifest>;

// Wire response for `GET /static/rectangles`: each size mapped to a signed,
// ready-to-fetch GLB URL. Distinct from StaticRectangleManifest (size -> S3
// *key*): the api signs those keys per ADR-0007 before handing them to the
// frontend, so the frontend never sees raw keys and never builds URLs itself
// (#65, ADR-0033 §2 "loaded from the manifest/keys, not hardcoded URLs").
export const StaticRectangleAssets = z.object({
  rectangles: z.record(StaticRectangleSizeSchema, z.string().url()),
});
export type StaticRectangleAssets = z.infer<typeof StaticRectangleAssets>;
